/**
 * Accounting Rules page composer.
 *
 * See file-header docs below for the full composition story.
 * One change in this revision: the RulesTabs + RulesToolbar are
 * wrapped in a single sticky container so they stay pinned to
 * the top of the scroll area under the page header while the
 * table scrolls underneath.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useRules } from "../../../hooks/useRules";
import {
  useRulesCustomization,
  visibleColumns,
} from "../../../hooks/useRulesCustomization";
import type { Rule, RuleTab } from "./rulesTypes";
import { filterRules, sortRules } from "./rulesUtils";
import { MOCK_BANK_RULES, MOCK_INTEGRATION_RULES } from "./rulesMockData";
import { RulesHeader } from "./components/RulesHeader";
import { RulesTabs } from "./components/RulesTabs";
import { RulesToolbar } from "./components/RulesToolbar";
import { RulesTable } from "./components/RulesTable";
import { RuleCard } from "./components/RuleCard";
import { RulesEmptyState } from "./components/RulesEmptyState";
import { CustomizeDrawer } from "./components/CustomizeDrawer";
import { RowActionsMenu } from "./components/RowActionsMenu";
import {
  ConfirmModal,
  type ConfirmModalVariant,
} from "./components/ConfirmModal";

export { RulesHeader };

const TOAST_DURATION_MS = 3200;

interface ConfirmState {
  variant: ConfirmModalVariant;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  statCount?: number;
  statLabel?: string;
  footerNote?: ReactNode;
  onConfirm: () => void;
}

export default function RulesPage() {
  const { isMobile } = useBreakpoint();
  const [tab, setTab] = useState<RuleTab>("bank");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRule, setMenuRule] = useState<Rule | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, updateRule, deleteRule, reorderRule } = useRules(tab);
  const { customization, update, toggleColumn, reorderColumns, reset } =
    useRulesCustomization(tab);

  useEffect(() => {
    setSelectedIds(new Set());
    setMenuAnchor(null);
    setMenuRule(null);
    setSearch("");
  }, [tab]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const visible = useMemo(
    () =>
      sortRules(
        filterRules(data, { search, status: "all" }),
        customization.sortBy,
        customization.sortDirection,
      ),
    [data, search, customization.sortBy, customization.sortDirection],
  );

  const activeCols = useMemo(() => visibleColumns(customization), [customization]);

  const bankCount = MOCK_BANK_RULES.length;
  const integrationCount = MOCK_INTEGRATION_RULES.length;

  const handleTogglePause = useCallback(
    (rule: Rule) => {
      const nextStatus = rule.status === "active" ? "paused" : "active";
      updateRule(rule.id, { status: nextStatus });
      setToast(nextStatus === "paused" ? "Rule paused." : "Rule resumed.");
    },
    [updateRule],
  );

  const handleEdit = useCallback(() => {
    setToast("Rule builder is coming in the next release.");
  }, []);

  const handleDuplicate = useCallback(() => {
    setToast("Rule builder is coming in the next release.");
  }, []);

  const handleMoveUp = useCallback(
    (rule: Rule) => {
      if (rule.priority <= 1) return;
      reorderRule(rule.id, rule.priority - 1);
      setToast("Priority updated.");
    },
    [reorderRule],
  );

  const handleMoveDown = useCallback(
    (rule: Rule) => {
      if (rule.priority >= data.length) return;
      reorderRule(rule.id, rule.priority + 1);
      setToast("Priority updated.");
    },
    [reorderRule, data.length],
  );

  const handleApplyToPast = useCallback((rule: Rule) => {
    setConfirm({
      variant: "info",
      title: "Apply this rule to past transactions?",
      description: (
        <>
          Vela will match <strong>{rule.name}</strong> against every
          transaction already in your ledger and update categories where the
          conditions match.
        </>
      ),
      confirmLabel: "Apply to past transactions",
      statCount: Math.max(1, rule.matchCount * 3),
      statLabel: "past transactions will be re-categorized",
      footerNote:
        "This runs instantly and can be reversed from Audit Log within 24 hours.",
      onConfirm: () => {
        setConfirm(null);
        setToast("Rule applied to past transactions.");
      },
    });
  }, []);

  const handleDelete = useCallback(
    (rule: Rule) => {
      setConfirm({
        variant: "danger",
        title: "Delete this rule?",
        description: (
          <>
            <strong>{rule.name}</strong> will no longer categorize new
            transactions. Past transactions it already categorized will keep
            their categories.
          </>
        ),
        confirmLabel: "Delete rule",
        footerNote:
          "To stop the rule from firing without losing it, pause it instead.",
        onConfirm: () => {
          deleteRule(rule.id);
          setConfirm(null);
          setToast("Rule deleted.");
        },
      });
    },
    [deleteRule],
  );

  const handleOpenMenu = useCallback((rule: Rule, anchor: HTMLElement) => {
    setMenuRule(rule);
    setMenuAnchor(anchor);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuRule(null);
  }, []);

  const handleSelectRule = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      setSelectedIds(selected ? new Set(visible.map((r) => r.id)) : new Set());
    },
    [visible],
  );

  const handleSortChange = useCallback(
    (field: typeof customization.sortBy) => {
      const sameField = customization.sortBy === field;
      update({
        sortBy: field,
        sortDirection:
          sameField && customization.sortDirection === "asc" ? "desc" : "asc",
      });
    },
    [customization.sortBy, customization.sortDirection, update],
  );

  return (
    <div style={pageStyle}>
      {data.length === 0 ? (
        <>
          <RulesTabs
            activeTab={tab}
            onTabChange={setTab}
            bankCount={bankCount}
            integrationCount={integrationCount}
          />
          <RulesEmptyState tab={tab} />
        </>
      ) : (
        <>
          {/* Sticky stack: tabs + toolbar stay pinned at the top of the
              scroll area while the table scrolls underneath. The page
              header above (rendered via AppLayout pageHeader slot) is
              already outside the scroll area. */}
          <div style={stickyStackStyle}>
            <RulesTabs
              activeTab={tab}
              onTabChange={setTab}
              bankCount={bankCount}
              integrationCount={integrationCount}
            />
            <RulesToolbar
              searchValue={search}
              onSearchChange={setSearch}
              onCustomizeToggle={() => setCustomizeOpen((o) => !o)}
              isCustomizeOpen={customizeOpen}
            />
          </div>

          {isMobile ? (
            <div>
              {visible.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onToggleStatus={(next) =>
                    updateRule(rule.id, { status: next ? "active" : "paused" })
                  }
                  onOpenMenu={(anchor) => handleOpenMenu(rule, anchor)}
                />
              ))}
            </div>
          ) : (
            <RulesTable
              rules={visible}
              visibleColumns={activeCols}
              sortBy={customization.sortBy}
              sortDirection={customization.sortDirection}
              rowDensity={customization.rowDensity}
              selectedIds={selectedIds}
              onSelectRule={handleSelectRule}
              onSelectAll={handleSelectAll}
              onSortChange={handleSortChange}
              onToggleStatus={(id, next) =>
                updateRule(id, { status: next ? "active" : "paused" })
              }
              onToggleAutoPost={(id, next) => updateRule(id, { autoPost: next })}
              onOpenMenu={handleOpenMenu}
            />
          )}
        </>
      )}

      <CustomizeDrawer
        isOpen={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        customization={customization}
        onUpdate={update}
        onToggleColumn={toggleColumn}
        onReorderColumns={reorderColumns}
        onReset={reset}
      />

      {menuRule && (
        <RowActionsMenu
          isOpen={menuAnchor !== null}
          rule={menuRule}
          anchorEl={menuAnchor}
          canMoveUp={menuRule.priority > 1}
          canMoveDown={menuRule.priority < data.length}
          onClose={handleCloseMenu}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onTogglePause={handleTogglePause}
          onApplyToPast={handleApplyToPast}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onDelete={handleDelete}
        />
      )}

      {confirm && (
        <ConfirmModal
          isOpen={true}
          variant={confirm.variant}
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          statCount={confirm.statCount}
          statLabel={confirm.statLabel}
          footerNote={confirm.footerNote}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {toast && <Toast message={toast} />}
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

const pageStyle: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  background: "#ffffff",
};

const stickyStackStyle: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 15,
  background: "#ffffff",
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: "#0E1A1A",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};